import type { Job } from '../job-queue.types';

// ===== JOB DISAPPEARANCE PROTECTION =====
// Cache of last seen processing jobs to prevent temporary disappearance
export const lastProcessingJobs: Map<string, { job: Job; timestamp: number }> =
  new Map();
export const PROCESSING_JOBS_RETENTION_MS = 60 * 1000; // Keep processing jobs for 60 seconds

// ===== TERMINAL STATE TRACKING =====
// Track when jobs reached terminal state to ignore stale events
export const terminalStateTimestamps: Map<string, number> = new Map();
const MAX_TERMINAL_TIMESTAMPS = 50; // Prevent memory leak
const CLOCK_SKEW_BUFFER_MS = 5000; // 5 seconds buffer for clock skew

/**
 * Record when a job reached terminal state
 */
export function recordTerminalState(jobId: string) {
  terminalStateTimestamps.set(jobId, Date.now());

  // Clean up old entries to prevent memory leak
  if (terminalStateTimestamps.size > MAX_TERMINAL_TIMESTAMPS) {
    const sortedEntries = [...terminalStateTimestamps.entries()].sort(
      (a, b) => a[1] - b[1]
    );
    const entriesToRemove = sortedEntries.slice(
      0,
      terminalStateTimestamps.size - MAX_TERMINAL_TIMESTAMPS
    );
    for (const [id] of entriesToRemove) {
      terminalStateTimestamps.delete(id);
    }
  }
}

/**
 * Check if an event is stale (arrived after terminal state was already recorded)
 */
export function isStaleEvent(jobId: string, eventTimestamp?: number): boolean {
  const terminalTime = terminalStateTimestamps.get(jobId);
  if (!terminalTime) return false;

  const checkTime = eventTimestamp ?? Date.now();
  // Event is stale if it has timestamp before terminal state was reached (with buffer)
  return checkTime < terminalTime - CLOCK_SKEW_BUFFER_MS;
}

/**
 * Validate job data from backend
 * Ensures required fields exist before updating store
 */
export function validateJobData(jobs: Job[]): boolean {
  if (!Array.isArray(jobs)) {
    return false;
  }

  // Check for required fields in each job
  return jobs.every(
    (job) =>
      typeof job.id === 'string' &&
      typeof job.status === 'string' &&
      typeof job.jobType === 'string' &&
      typeof job.conceptId === 'string' &&
      typeof job.projectId === 'string'
  );
}
